import { useEffect, useRef, useState } from 'react'
import { Button } from './ui/button'

interface SignaturePadProps {
  // Called with a PNG data URL when the user confirms their signature
  onDone: (dataUrl: string) => void
  onCancel?: () => void
  height?: number
}

/**
 * Minimal drawn-signature canvas (flight log captain sign-off). Pointer-events
 * based so it works with touch, stylus and mouse; scaled by devicePixelRatio
 * so strokes stay crisp on retina screens. Output is a PNG data URL — small
 * enough (~5–20 KB) to store inline in the flight log JSON.
 */
export function SignaturePad({ onDone, onCancel, height = 160 }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const [hasInk, setHasInk] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    // Ink color from the app's primary content token so the stroke matches
    // the design system in both themes.
    ctx.strokeStyle =
      getComputedStyle(canvas).getPropertyValue('color').trim() || '#1a1a1a'
  }, [])

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    e.currentTarget.setPointerCapture(e.pointerId)
    drawingRef.current = true
    const { x, y } = pointFromEvent(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    // A tap should still leave a dot
    ctx.lineTo(x + 0.1, y + 0.1)
    ctx.stroke()
    setHasInk(true)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = pointFromEvent(e)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const handlePointerUp = () => {
    drawingRef.current = false
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasInk(false)
  }

  const handleDone = () => {
    const canvas = canvasRef.current
    if (!canvas || !hasInk) return
    onDone(canvas.toDataURL('image/png'))
  }

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        className="w-full rounded-lg border border-border-L2 bg-surface-L0 text-content-primary-bold touch-none"
        style={{ height }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="secondary" size="mini" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="mini"
          onClick={handleClear}
          disabled={!hasInk}
        >
          Clear
        </Button>
        <Button type="button" variant="primary" size="mini" onClick={handleDone} disabled={!hasInk}>
          Done
        </Button>
      </div>
    </div>
  )
}
